import { Controller } from "@hotwired/stimulus";

const QUESTION_EXPAND_EVENT = "question-card:expand";
const QUESTION_COLLAPSE_EVENT = "question-card:collapse";
const QUESTION_SETTINGS_EVENT = "question-card:settings";
const SECTION_SELECT_EVENT = "outline-rail:section-select";
const FRAME_ID = "builder_settings";
// The settings rail (see edit.html.erb) is a static column once the page
// sheet is @4xl wide; in a narrower room it stays hidden until a question or
// section is focused, then opens as a bottom sheet on a phone or in flow on a
// narrow desktop sheet. The rail carries `hidden` in both cases; the column
// simply outranks it with @4xl:block, which is how this controller tells the
// two apart without measuring the window.
const SHEET_ID = "builder_settings_sheet";
const BACKDROP_ID = "builder_settings_backdrop";
const CLOSE_ID = "builder_settings_close";

export default class extends Controller<HTMLElement> {
  static values = {
    questionUrlTemplate: String,
    sectionUrlTemplate: String,
  };

  declare readonly questionUrlTemplateValue: string;
  declare readonly sectionUrlTemplateValue: string;

  private placeholderHtml = "";
  private boundOnQuestionExpand = this.onQuestionExpand.bind(this);
  private boundOnQuestionCollapse = this.onQuestionCollapse.bind(this);
  private boundOnQuestionSettings = this.onQuestionSettings.bind(this);
  private boundOnSectionSelect = this.onSectionSelect.bind(this);
  private boundCloseSheet = this.closeSheet.bind(this);
  private boundLoading = this.onFetchStart.bind(this);
  private boundLoaded = () => this.frame()?.removeAttribute("data-loading");

  connect() {
    // Capture the server-rendered placeholder so a collapse can restore it.
    const frame = this.frame();
    if (frame) this.placeholderHtml = frame.innerHTML;
    frame?.addEventListener("turbo:before-fetch-request", this.boundLoading);
    frame?.addEventListener("turbo:frame-load", this.boundLoaded);
    frame?.addEventListener("turbo:fetch-request-error", this.boundLoaded);

    window.addEventListener(QUESTION_EXPAND_EVENT, this.boundOnQuestionExpand);
    window.addEventListener(QUESTION_COLLAPSE_EVENT, this.boundOnQuestionCollapse);
    window.addEventListener(QUESTION_SETTINGS_EVENT, this.boundOnQuestionSettings);
    window.addEventListener(SECTION_SELECT_EVENT, this.boundOnSectionSelect);

    // The sheet backdrop + close button live outside this controller's element
    // (they're siblings in the aside), so wire them by id rather than via a
    // Stimulus action.
    document.getElementById(BACKDROP_ID)?.addEventListener("click", this.boundCloseSheet);
    document.getElementById(CLOSE_ID)?.addEventListener("click", this.boundCloseSheet);
  }

  disconnect() {
    window.removeEventListener(QUESTION_EXPAND_EVENT, this.boundOnQuestionExpand);
    window.removeEventListener(QUESTION_COLLAPSE_EVENT, this.boundOnQuestionCollapse);
    window.removeEventListener(QUESTION_SETTINGS_EVENT, this.boundOnQuestionSettings);
    window.removeEventListener(SECTION_SELECT_EVENT, this.boundOnSectionSelect);
    document.getElementById(BACKDROP_ID)?.removeEventListener("click", this.boundCloseSheet);
    document.getElementById(CLOSE_ID)?.removeEventListener("click", this.boundCloseSheet);
    this.frame()?.removeEventListener("turbo:before-fetch-request", this.boundLoading);
    this.frame()?.removeEventListener("turbo:frame-load", this.boundLoaded);
    this.frame()?.removeEventListener("turbo:fetch-request-error", this.boundLoaded);
    this.unlockBodyScroll();
  }

  private onQuestionExpand(event: Event) {
    const detail = (event as CustomEvent).detail as
      | { questionId?: string; sectionId?: string }
      | undefined;
    if (!detail?.questionId || !detail?.sectionId) return;

    const url = this.questionUrlTemplateValue
      .replace("__SECTION__", detail.sectionId)
      .replace("__QUESTION__", detail.questionId);
    // Focusing a question loads its settings into the frame so the desktop
    // rail reflects it, but does NOT open the mobile sheet — on mobile that's
    // an explicit action (the card's Settings button → onQuestionSettings),
    // so tapping a question only expands its inline form.
    this.loadFrame(url);
  }

  // Mobile "Settings" button on the open question card. Load this question's
  // settings and reveal the sheet.
  private onQuestionSettings(event: Event) {
    const detail = (event as CustomEvent).detail as
      | { questionId?: string; sectionId?: string }
      | undefined;
    if (!detail?.questionId || !detail?.sectionId) return;

    const url = this.questionUrlTemplateValue
      .replace("__SECTION__", detail.sectionId)
      .replace("__QUESTION__", detail.questionId);
    this.loadFrame(url);
    this.openSheet();
  }

  // A card was minimized — drop its settings and restore the placeholder so
  // the rail doesn't keep showing a question that is no longer focused.
  private onQuestionCollapse() {
    this.restorePlaceholder();
    this.hideSheet();
  }

  private onSectionSelect(event: Event) {
    const detail = (event as CustomEvent).detail as
      | { sectionId?: string }
      | undefined;
    if (!detail?.sectionId) return;

    const url = this.sectionUrlTemplateValue.replace(
      "__SECTION__",
      detail.sectionId,
    );
    // Sections have no inline canvas editor, so selecting one (from the outline
    // sheet) opens the settings sheet directly on mobile.
    this.loadFrame(url);
    this.openSheet();
  }

  // Mobile sheet close (X button or backdrop tap). Collapse the focused
  // question if one is open (which fires question-card:collapse → restores the
  // placeholder), then hide the sheet either way (covers section focus too).
  closeSheet() {
    const openCard = document.querySelector<HTMLElement>(
      '[data-controller~="question-card"] [data-question-card-target="form"]:not(.hidden)',
    );
    if (openCard) {
      window.dispatchEvent(new CustomEvent(QUESTION_COLLAPSE_EVENT));
    }
    this.restorePlaceholder();
    this.hideSheet();
  }

  // Marks the frame as loading for the rail's overlay, which keys off this
  // rather than Turbo's [busy] so auto-save submits don't show it: a frame
  // load fetches from the frame itself, a form submit from the form inside.
  private onFetchStart(event: Event) {
    if (event.target === this.frame()) this.frame()?.setAttribute("data-loading", "");
  }

  private loadFrame(url: string) {
    this.frame()?.setAttribute("src", url);
  }

  private frame(): HTMLElement | null {
    return document.getElementById(FRAME_ID);
  }

  private restorePlaceholder() {
    const frame = this.frame();
    if (!frame) return;
    frame.removeAttribute("data-loading");
    frame.removeAttribute("src");
    frame.innerHTML = this.placeholderHtml;
  }

  private openSheet() {
    if (this.railIsColumn()) return;
    document.getElementById(SHEET_ID)?.classList.remove("hidden");
    document.getElementById(BACKDROP_ID)?.classList.remove("hidden");
    this.lockBodyScroll();
  }

  private hideSheet() {
    document.getElementById(SHEET_ID)?.classList.add("hidden");
    document.getElementById(BACKDROP_ID)?.classList.add("hidden");
    this.unlockBodyScroll();
  }

  // A rail that renders while still carrying `hidden` is the @4xl column:
  // there is nothing to open. Absent from the page, likewise.
  private railIsColumn(): boolean {
    const sheet = document.getElementById(SHEET_ID);
    if (!sheet) return true;
    if (!sheet.classList.contains("hidden")) return false;
    return sheet.checkVisibility?.() ?? sheet.offsetParent !== null;
  }

  private lockBodyScroll() {
    document.body.style.overflow = "hidden";
  }

  private unlockBodyScroll() {
    document.body.style.overflow = "";
  }
}
